import {
  Button,
  Link,
  TableToolbar,
  Table,
  Typography,
} from 'melp-design/components';
import { List, ListItem, Stack } from '@mui/material';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DateRangeFilter,
  FilterButton,
  Filters,
  SourcesFilter,
  SelectFilter,
} from 'components/filters';
import { formatCurrency, formatList } from 'utils/format';
import { useModalContext } from 'store/modal';
import {
  EmployeeExpensesFilters,
  employeeExpensesFiltersSchema,
  useEmployeeExpenses,
} from 'store/employees';
import { APP, ROUTES } from 'config';
import { RECOGNITION_SHOP_ID } from 'store/recognitions';
import { COLOR_BY_STATUS, expenseStatuses } from 'store/expenses';
import { useDefaultCurrency, useMe } from 'state/Administrators';
import { shopItemCategories } from 'types/MarketplaceItems';
import { useRouteParams } from 'utils/useIdParam';
import { fromCents } from 'utils/general';
import { EditExpenseModal } from '.';

const INITIAL_FILTERS = employeeExpensesFiltersSchema.parse({});

export const ExpensesTable = () => {
  const { t, i18n } = useTranslation();
  const { openModal } = useModalContext();
  const { currency } = useDefaultCurrency();
  const { hasViewPermission } = useMe();
  const { id: employeeId } = useRouteParams(ROUTES.employees.details);

  const [filters, setFilters] =
    useState<EmployeeExpensesFilters>(INITIAL_FILTERS);

  const { data: expenses, isLoading } = useEmployeeExpenses(
    employeeId,
    filters,
  );

  return (
    <>
      <TableToolbar>
        <Stack direction="row" justifyContent="end" gap="10px">
          <Filters
            value={filters}
            onChange={setFilters}
            initialValues={INITIAL_FILTERS}
          >
            {({ value, initialValues, setFilter }) => (
              <>
                <FilterButton
                  label={t('marketplaceOrders.orderDate')}
                  value={value.createdAt}
                  initialValue={initialValues.createdAt}
                  onChange={(v) => setFilter('createdAt', v)}
                >
                  {({ value, applyFilter, clearFilter, close }) => (
                    <DateRangeFilter
                      value={value}
                      onApply={applyFilter}
                      onClear={clearFilter}
                      onCancel={close}
                    />
                  )}
                </FilterButton>

                <FilterButton
                  label={t('expenses.expense_date')}
                  value={value.date}
                  initialValue={initialValues.date}
                  onChange={(v) => setFilter('date', v)}
                >
                  {({ value, applyFilter, clearFilter, close }) => (
                    <DateRangeFilter
                      value={value}
                      onApply={applyFilter}
                      onClear={clearFilter}
                      onCancel={close}
                    />
                  )}
                </FilterButton>

                <FilterButton
                  label={t('wallet.employeeWalletFundsSource')}
                  value={value.source}
                  initialValue={initialValues.source}
                  onChange={(v) => setFilter('source', v)}
                >
                  {({ value, applyFilter, clearFilter, close }) => (
                    <SourcesFilter
                      value={{ value }}
                      onApplyFilter={({ value }) => applyFilter(value)}
                      onClearFilter={clearFilter}
                      onCancel={close}
                    />
                  )}
                </FilterButton>

                <FilterButton
                  label={t('wallet.employeeWalletItemCategory')}
                  value={value.categories}
                  initialValue={initialValues.categories}
                  onChange={(v) => setFilter('categories', v)}
                >
                  {({ value, applyFilter, clearFilter, close }) => (
                    <SelectFilter
                      value={{ value }}
                      onApplyFilter={({ value }) => applyFilter(value)}
                      onClearFilter={clearFilter}
                      onCancel={close}
                      multiple
                      options={shopItemCategories.map((category) => ({
                        key: category,
                        label: t(`marketplaceItems.itemCategory-${category}`),
                      }))}
                    />
                  )}
                </FilterButton>

                <FilterButton
                  label={t('common.status')}
                  value={value.status}
                  initialValue={initialValues.status}
                  onChange={(v) => setFilter('status', v)}
                >
                  {({ value, applyFilter, clearFilter, close }) => (
                    <SelectFilter
                      value={{ value }}
                      onApplyFilter={({ value }) => applyFilter(value)}
                      onClearFilter={clearFilter}
                      onCancel={close}
                      multiple
                      options={expenseStatuses.map((status) => ({
                        key: status,
                        label: t(`expenses.status.${status}`),
                      }))}
                      searchable={false}
                    />
                  )}
                </FilterButton>
              </>
            )}
          </Filters>
        </Stack>
      </TableToolbar>

      <Table
        data={expenses?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'date',
            header: t('marketplaceOrders.orderDate'),
            cell: ({ row: { original: expense } }) =>
              moment(expense.createdAt).format('l'),
          },
          {
            id: 'expenseDate',
            header: t('expenses.expense_date'),
            cell: ({ row: { original: expense } }) =>
              expense.date ? moment(expense.date).format('l') : '-',
          },
          {
            id: 'source',
            header: t('wallet.employeeWalletFundsSource'),
            cell: ({ row: { original: expense } }) => (
              <List sx={{ py: 0 }}>
                {expense.source.map((s) => (
                  <ListItem sx={{ p: 0, mb: 1 }} key={s.id}>
                    {s.id === RECOGNITION_SHOP_ID ? (
                      hasViewPermission(['employeeRecognition']) ? (
                        <Link
                          label={s.name}
                          to={ROUTES.recognitions.settings}
                        />
                      ) : (
                        <Typography>{s.name}</Typography>
                      )
                    ) : hasViewPermission(['benefits']) ? (
                      <Link
                        label={s.name}
                        to={ROUTES.benefits.details.replace(':id', s.id)}
                      />
                    ) : (
                      <Typography>{s.name}</Typography>
                    )}
                  </ListItem>
                ))}
              </List>
            ),
          },
          {
            id: 'description',
            header: t('expenses.table.description.title'),
            cell: ({ row: { original: expense } }) => expense.description,
          },
          {
            id: 'categories',
            header: t('expenses.table.categories.title'),
            cell: ({ row: { original: expense } }) =>
              formatList(
                i18n.language,
                expense.categories.map((c) =>
                  t(`marketplaceItems.itemCategory-${c}`),
                ),
              ),
          },
          {
            id: 'amount',
            header: t('employees.reserved'),
            meta: { align: 'right' },
            cell: ({ row: { original: expense } }) =>
              formatCurrency(i18n.language, expense.amount, currency),
          },
          {
            id: 'status',
            header: t('expenses.table.status.title'),
            cell: ({ row: { original: expense } }) => (
              <Typography color={COLOR_BY_STATUS[expense.status]}>
                {t(`expenses.status.${expense.status}`)}
              </Typography>
            ),
          },
          {
            id: 'actions',
            cell: ({ row: { original: expense } }) => {
              if (expense.type !== 'manual') return null;

              return (
                <Button
                  label={t('common.edit')}
                  variant="neutral-outline"
                  size="sm"
                  onClick={() => {
                    openModal({
                      component: EditExpenseModal,
                      props: {
                        expenseId: expense.id,
                        employeeId,
                        values: {
                          date: expense.date,
                          status: expense.status,
                          amount: fromCents(expense.amount),
                          benefits: expense.source
                            .filter(
                              (source) => source.id !== RECOGNITION_SHOP_ID,
                            )
                            .map((source) => source.id),
                          useRecognition: !!expense.source.filter(
                            (source) => source.id === RECOGNITION_SHOP_ID,
                          ).length,
                          categories: expense.categories,
                          description: expense.description,
                        },
                      },
                      size: 'lg',
                    });
                  }}
                />
              );
            },
          },
        ]}
        pagination={
          expenses && {
            count: expenses.total,
            page: expenses.page,
            pageSize: expenses.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              setFilters((prev) => ({ ...prev, page, pageSize }));
            },
          }
        }
      />
    </>
  );
};
